import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle, Lock, Mail, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');

  console.log("Referral code from URL:", referralCode);

  const handleSubmit = async (e: FormEvent) => {
  e.preventDefault();
  setError('');
  setIsLoading(true);

  try {
    // First sign up the user
    const { error: signUpError } = await signUp(email, password);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }

    // Get the new user's ID
    const { data: { user: newUser } } = await supabase.auth.getUser();

    if (!newUser?.id) {
      throw new Error('Failed to get user ID');
    }

    // If there's a referral code, process it
    if (referralCode) {
      const decodedReferralCode = decodeURIComponent(referralCode).trim().toLowerCase();
      console.log("Using Referral Code:", decodedReferralCode);

      try {
        // Get the referrer's user ID from the referral code
        const { data: referralData, error: referralError } = await supabase
          .from('referral_codes')
          .select('user_id')
          .eq('code', decodedReferralCode)
          .maybeSingle();

        console.log("Referral Data Response:", referralData);

        if (referralError) {
          console.error('Error fetching referral code:', referralError);
          throw new Error('Invalid referral code');
        }

        if (!referralData || !referralData.user_id) {
          console.error('No referral code found');
          throw new Error('Invalid referral code');
        }

        console.log("Referrer User ID:", referralData.user_id);

        // Create referral record
        const { data: referral, error: insertError } = await supabase
          .from('referrals')
          .insert({
            referrer_id: referralData.user_id,
            referred_id: newUser.id,
            status: 'pending'
          })
          .select()
          .single();

        console.log("Referral Insert Response:", referral);

        if (insertError) {
          console.error('Error creating referral:', insertError);
          throw new Error('Failed to process referral');
        }

        // Complete the referral to award bonus conversions
        const { error: completeError } = await supabase
          .rpc('complete_referral', { referral_id: referral.id });

        if (completeError) {
          console.error('Error completing referral:', completeError);
          throw new Error('Failed to process referral reward');
        }

        console.log("Referral completed successfully.");
      } catch (referralError) {
        console.error('Error processing referral:', referralError);
      }
    }

    navigate('/dashboard');
  } catch (err) {
    console.error('Unexpected error during sign up:', err);
    setError('An unexpected error occurred. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

  // Add referral info message if code is present
  const ReferralInfo = () => {
    if (!referralCode) return null;

    return (
      <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-green-700">
              You've been invited! Sign up to get your free conversions.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <ReferralInfo />
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Must be at least 6 characters
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating account...</span>
                  </div>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}